import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import SuperAdminLayout from '../components/SuperAdminLayout';
import { useCustomDialog } from '../context/DialogContext';
import '../styles/superadmin.css';

const BASE = 'https://menu.searchisend.in';

const ManageAdmins = () => {
    const navigate = useNavigate();
    const { confirm: customConfirm } = useCustomDialog();
    const token = localStorage.getItem('saas_superadmin_token');
    
    const [admins, setAdmins] = useState([]);
    const [currentUserId, setCurrentUserId] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');
    
    const [newAdmin, setNewAdmin] = useState({ username: '', password: '' });
    const [editingAdminId, setEditingAdminId] = useState(null);
    const [passwordData, setPasswordData] = useState({ new_password: '', confirm_password: '' });

    useEffect(() => {
        if (!token) {
            navigate('/superadmin_v2_secure_login');
            return;
        }
        fetchAdmins();
        // eslint-disable-next-line
    }, []);

    const fetchAdmins = async () => {
        setLoading(true);
        try {
            const res = await fetch(`${BASE}/apimenu/v2/v2_super_dashboard.php?action=fetch_superadmins`, {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            const data = await res.json();
            if (data.success) {
                setAdmins(data.admins);
                setCurrentUserId(data.current_user_id);
            } else {
                setError(data.message || 'Error fetching admins');
            }
        } catch (err) {
            setError('Network error');
        } finally {
            setLoading(false);
        }
    };

    const handleCreate = async (e) => {
        e.preventDefault();
        setError(''); setSuccess('');
        try {
            const res = await fetch(`${BASE}/apimenu/v2/v2_super_dashboard.php?action=create_superadmin`, {
                method: 'POST',
                headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
                body: JSON.stringify(newAdmin)
            });
            const data = await res.json();
            if (data.success) {
                setSuccess('Super admin created successfully!');
                setNewAdmin({ username: '', password: '' });
                fetchAdmins();
            } else {
                setError(data.message);
            }
        } catch (err) {
            setError('Network error');
        }
    };

    const handleDelete = async (id) => {
        if (!await customConfirm("Are you sure you want to delete this super admin?", "Revoke Superadmin Access", "danger")) return;
        setError(''); setSuccess('');
        try {
            const res = await fetch(`${BASE}/apimenu/v2/v2_super_dashboard.php?action=delete_superadmin`, {
                method: 'POST',
                headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
                body: JSON.stringify({ id })
            });
            const data = await res.json();
            if (data.success) {
                setSuccess('Super admin deleted successfully');
                fetchAdmins();
            } else {
                setError(data.message);
            }
        } catch (err) {
            setError('Network error');
        }
    };

    const handleUpdatePassword = async (e, id) => {
        e.preventDefault();
        if (passwordData.new_password !== passwordData.confirm_password) {
            setError("Passwords do not match");
            return;
        }
        setError(''); setSuccess('');
        try {
            const res = await fetch(`${BASE}/apimenu/v2/v2_super_dashboard.php?action=update_superadmin_password`, {
                method: 'POST',
                headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
                body: JSON.stringify({ id, new_password: passwordData.new_password })
            });
            const data = await res.json();
            if (data.success) {
                setSuccess('Password updated successfully');
                setEditingAdminId(null);
                setPasswordData({ new_password: '', confirm_password: '' });
            } else {
                setError(data.message);
            }
        } catch (err) {
            setError('Network error');
        }
    };

    return (
        <SuperAdminLayout>
                    <div style={{ maxWidth: '900px', margin: '0 auto' }}>
                        <div style={{ marginBottom: '2rem' }}>
                            <h2 className="sa-page-title">Manage Super Admins</h2>
                            <p className="sa-page-subtitle">Control who has access to the global SaaS platform.</p>
                        </div>

                        {error && <div style={{ background: 'var(--danger-bg)', color: 'var(--danger)', padding: '12px 16px', borderRadius: 'var(--radius-sm)', marginBottom: '20px', border: '1px solid var(--danger)', fontWeight: 500 }}><i className="fas fa-exclamation-circle"></i> {error}</div>}
                        {success && <div style={{ background: 'var(--success-bg)', color: 'var(--success)', padding: '12px 16px', borderRadius: 'var(--radius-sm)', marginBottom: '20px', border: '1px solid var(--success)', fontWeight: 500 }}><i className="fas fa-check-circle"></i> {success}</div>}

                        <div className="sa-panel" style={{ padding: '2rem', marginBottom: '2.5rem' }}>
                            <h3 className="sa-panel-title" style={{ borderBottom: 'none', marginBottom: '1.5rem', color: 'var(--text-primary)' }}><i className="fas fa-user-plus"></i> Add New Super Admin</h3>
                            <form onSubmit={handleCreate} style={{ display: 'grid', gridTemplateColumns: '1fr 1fr auto', gap: '20px', alignItems: 'end' }}>
                                <div className="sa-form-group" style={{ marginBottom: 0 }}>
                                    <label className="sa-form-label">Username</label>
                                    <input type="text" className="sa-input" style={{ width: '100%', background: 'var(--bg-card)' }} value={newAdmin.username} onChange={e => setNewAdmin({...newAdmin, username: e.target.value})} required placeholder="Enter username" />
                                </div>
                                <div className="sa-form-group" style={{ marginBottom: 0 }}>
                                    <label className="sa-form-label">Password</label>
                                    <input type="password" className="sa-input" style={{ width: '100%', background: 'var(--bg-card)' }} value={newAdmin.password} onChange={e => setNewAdmin({...newAdmin, password: e.target.value})} required placeholder="Enter secure password" />
                                </div>
                                <button type="submit" className="sa-primary-btn" style={{ height: '42px', padding: '0 25px' }}><i className="fas fa-shield-alt"></i> Grant Access</button>
                            </form>
                        </div>

                        <div className="sa-panel" style={{ padding: 0, overflow: 'hidden' }}>
                            <div className="sa-table-container" style={{ margin: 0, border: 'none', borderRadius: 0, boxShadow: 'none' }}>
                                <table className="sa-table" style={{ margin: 0 }}>
                                    <thead>
                                        <tr>
                                            <th style={{ width: '60px' }}>ID</th>
                                            <th>Admin Account</th>
                                            <th style={{ textAlign: 'right' }}>Actions</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {loading ? (
                                            <tr><td colSpan="3" style={{ textAlign: 'center', padding: '3rem', color: 'var(--text-secondary)' }}><i className="fas fa-spinner fa-spin"></i> Loading admins...</td></tr>
                                        ) : admins.map(admin => (
                                            <React.Fragment key={admin.id}>
                                                <tr>
                                                    <td style={{ color: 'var(--text-secondary)', fontWeight: 500 }}>#{admin.id}</td>
                                                    <td>
                                                        <div style={{ display: 'flex', alignItems: 'center', gap: '15px' }}>
                                                            <div style={{ width: '40px', height: '40px', borderRadius: '50%', background: 'rgba(249, 115, 22, 0.1)', color: 'var(--accent-primary)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 'bold' }}>
                                                                {admin.username.charAt(0).toUpperCase()}
                                                            </div>
                                                            <div>
                                                                <strong style={{ color: 'var(--text-primary)', fontSize: '1rem' }}>{admin.username}</strong>
                                                                {admin.id === currentUserId && <span style={{ marginLeft: '10px', fontSize: '0.75rem', background: 'var(--success-bg)', color: 'var(--success)', padding: '3px 8px', borderRadius: '10px', fontWeight: 600 }}>Active Now</span>}
                                                            </div>
                                                        </div>
                                                    </td>
                                                    <td style={{ textAlign: 'right' }}>
                                                        <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end' }}>
                                                            <button onClick={() => { setEditingAdminId(admin.id); setPasswordData({ new_password: '', confirm_password: '' }); }} className="sa-action-btn primary" title="Edit Password">
                                                                <i className="fas fa-key"></i> Edit Key
                                                            </button>
                                                            {admin.id !== currentUserId && (
                                                                <button onClick={() => handleDelete(admin.id)} className="sa-action-btn danger" title="Revoke Access">
                                                                    <i className="fas fa-trash"></i>
                                                                </button>
                                                            )}
                                                        </div>
                                                    </td>
                                                </tr>
                                                {editingAdminId === admin.id && (
                                                    <tr style={{ backgroundColor: 'var(--bg-dark)' }}>
                                                        <td colSpan="3" style={{ padding: '20px 25px' }}>
                                                            <form onSubmit={(e) => handleUpdatePassword(e, admin.id)} style={{ display: 'flex', gap: '15px', alignItems: 'end', background: 'var(--bg-card)', padding: '15px', borderRadius: 'var(--radius-sm)', border: '1px solid var(--accent-primary)' }}>
                                                                <div className="sa-form-group" style={{ marginBottom: 0, flex: 1 }}>
                                                                    <label className="sa-form-label"><i className="fas fa-lock"></i> New Password</label>
                                                                    <input type="password" className="sa-input" style={{ width: '100%' }} value={passwordData.new_password} onChange={e => setPasswordData({...passwordData, new_password: e.target.value})} required placeholder="Enter new password" />
                                                                </div>
                                                                <div className="sa-form-group" style={{ marginBottom: 0, flex: 1 }}>
                                                                    <label className="sa-form-label"><i className="fas fa-lock"></i> Confirm Password</label>
                                                                    <input type="password" className="sa-input" style={{ width: '100%' }} value={passwordData.confirm_password} onChange={e => setPasswordData({...passwordData, confirm_password: e.target.value})} required placeholder="Confirm new password" />
                                                                </div>
                                                                <div style={{ display: 'flex', gap: '10px' }}>
                                                                    <button type="submit" className="sa-primary-btn" style={{ background: 'var(--accent-primary)', height: '42px', padding: '0 20px' }}>Save</button>
                                                                    <button type="button" onClick={() => setEditingAdminId(null)} className="sa-secondary-btn" style={{ height: '42px', padding: '0 20px' }}>Cancel</button>
                                                                </div>
                                                            </form>
                                                        </td>
                                                    </tr>
                                                )}
                                            </React.Fragment>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    </div>
        </SuperAdminLayout>
    );
};

export default ManageAdmins;

